import resolver, {ResolverSettings} from './utils/resolver';
import {
  MultiPayment,
  OrderHistory,
  PaymentsCart,
  PaymentsSettings,
  ReservationPayment,
} from '../utils/types';

type CreateMultiPaymentsPayload = {
  reservation: string;
  session_id?: string;
  requested_specification: {
    is_ready_to_pay_for_all?: boolean;
    requested_reservation_payment_ids?: string[];
    is_deposit_payment_requested?: boolean;
    is_damage_protection_payment_requested?: boolean;
    is_taxes_payment_requested?: boolean;
  };
};

type CheckExpiredReservationPaymentsPayload = {reservation_id: string};
type CheckExpiredReservationPaymentsResponse = {
  expired_payments: ReservationPayment[];
  has_expired_payments: boolean;
};

class PaymentsApi {
  readonly ENDPOINTS = {
    paymentsSettings: (params = '') => `payments-settings/?${params}`,
    reservationPayments: (id: string, sessionId?: string | null) =>
      `reservation-payments/?reservation=${id}${
        sessionId ? `&session_id=${sessionId}` : ''
      }`,
    checkExpiredReservationPayments: () => `reservation-payments/check-expired/`,
    multiPayments: () => `multi-payments/`,
    oneMultiPayment: (id: string) => `multi-payments/${id}/`,
    paymentsCart: (params = '') => `payments-cart/?${params}`,
    orderHistory: (params = '') => `order-history/?${params}`,
  };

  async getPaymentsSettings(params: string, resolverSettings?: ResolverSettings) {
    const response = await resolver<PaymentsSettings>(
      this.ENDPOINTS.paymentsSettings(params),
      null,
      resolverSettings,
    );
    return response.body;
  }

  async getReservationPayments(
    id: string,
    sessionId?: string | null,
    resolverSettings?: ResolverSettings,
  ) {
    const response = await resolver<ReservationPayment[]>(
      this.ENDPOINTS.reservationPayments(id, sessionId),
      null,
      resolverSettings,
    );
    return response.body;
  }

  async checkExpiredReservationPayments(
    payload: CheckExpiredReservationPaymentsPayload,
    resolverSettings?: ResolverSettings,
  ) {
    const response = await resolver<CheckExpiredReservationPaymentsResponse>(
      this.ENDPOINTS.checkExpiredReservationPayments(),
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      resolverSettings,
    );
    return response.body;
  }

  async createMultiPayments(
    payload: CreateMultiPaymentsPayload,
    resolverSettings?: ResolverSettings,
  ) {
    const response = await resolver<MultiPayment>(
      this.ENDPOINTS.multiPayments(),
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      {version: 4, ...resolverSettings},
    );
    return response.body;
  }

  async getMultiPayment(id: string, resolverSettings?: ResolverSettings) {
    const response = await resolver<MultiPayment>(
      this.ENDPOINTS.oneMultiPayment(id),
      null,
      {
        version: 4,
        ...resolverSettings,
      },
    );
    return response.body;
  }

  async getPaymentsCart(params: string, resolverSettings?: ResolverSettings) {
    const response = await resolver<PaymentsCart>(
      this.ENDPOINTS.paymentsCart(params),
      null,
      {
        version: 4,
        ...resolverSettings,
      },
    );
    return response.body;
  }

  async getOrderHistory(params: string, resolverSettings?: ResolverSettings) {
    const response = await resolver<OrderHistory>(
      this.ENDPOINTS.orderHistory(params),
      null,
      {
        version: 4,
        ...resolverSettings,
      },
    );
    return response.body;
  }
}

export {PaymentsApi};
export type {CreateMultiPaymentsPayload};
